import React, { useEffect, useState, useCallback } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { FileClock, Loader2, RefreshCw, FileText, Sheet, FileType2, Presentation, Image as ImageIcon, Braces, Code2, Globe, Package, FileSpreadsheet, Download } from 'lucide-react';

const FMT_META = {
  pdf: { icon: FileText, color: 'text-rose-400', label: 'PDF' },
  xlsx: { icon: Sheet, color: 'text-emerald-400', label: 'Excel' },
  csv: { icon: FileSpreadsheet, color: 'text-green-400', label: 'CSV' },
  docx: { icon: FileType2, color: 'text-blue-400', label: 'Word' },
  pptx: { icon: Presentation, color: 'text-orange-400', label: 'PowerPoint' },
  png: { icon: ImageIcon, color: 'text-fuchsia-400', label: 'PNG' },
  json: { icon: Braces, color: 'text-yellow-400', label: 'JSON' },
  xml: { icon: Code2, color: 'text-amber-400', label: 'XML' },
  html: { icon: Globe, color: 'text-sky-400', label: 'HTML' },
  zip: { icon: Package, color: 'text-violet-400', label: 'Package' },
};

const FMT_OPTIONS = ['all', 'pdf', 'xlsx', 'csv', 'docx', 'pptx', 'png', 'json', 'xml', 'html'];

export default function ExportAuditPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [fmt, setFmt] = useState('all');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await api.getExportLogs(fmt === 'all' ? undefined : fmt);
      setLogs(r?.logs || []);
    } catch {
      setLogs([]);
    } finally {
      setLoading(false);
    }
  }, [fmt]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="p-6 max-w-6xl mx-auto space-y-5" data-testid="export-audit-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-primary/15 flex items-center justify-center">
            <FileClock className="w-5 h-5 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-heading font-bold text-foreground">Export Audit</h1>
            <p className="text-sm text-muted-foreground">Who exported which report or dashboard, in what format, and when.</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Select value={fmt} onValueChange={setFmt}>
            <SelectTrigger className="w-36 h-9 rounded-lg text-sm" data-testid="export-audit-format-filter">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {FMT_OPTIONS.map(o => (
                <SelectItem key={o} value={o} data-testid={`export-audit-fmt-${o}`}>
                  {o === 'all' ? 'All formats' : (FMT_META[o]?.label || o.toUpperCase())}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={load} className="rounded-lg h-9 gap-1.5" data-testid="export-audit-refresh">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </Button>
        </div>
      </div>

      <Card className="p-0 overflow-hidden">
        {loading ? (
          <div className="flex items-center justify-center py-16 text-muted-foreground gap-2" data-testid="export-audit-loading">
            <Loader2 className="w-5 h-5 animate-spin" /> Loading export history…
          </div>
        ) : logs.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-muted-foreground gap-2" data-testid="export-audit-empty">
            <Download className="w-8 h-8 opacity-40" />
            <p className="text-sm">No exports recorded yet.</p>
          </div>
        ) : (
          <div className="divide-y divide-border/40" data-testid="export-audit-list">
            <div className="grid grid-cols-12 gap-3 px-4 py-2.5 text-[11px] uppercase tracking-wide text-muted-foreground/70 font-semibold bg-muted/30">
              <div className="col-span-4">Asset</div>
              <div className="col-span-2">Format</div>
              <div className="col-span-3">Exported by</div>
              <div className="col-span-3">When</div>
            </div>
            {logs.map((log, i) => {
              const f = (log.metadata?.format || '').toLowerCase();
              const meta = FMT_META[f] || { icon: Download, color: 'text-muted-foreground', label: f.toUpperCase() || '—' };
              const Icon = meta.icon;
              const when = log.timestamp ? new Date(log.timestamp).toLocaleString() : '—';
              return (
                <div key={i} className="grid grid-cols-12 gap-3 px-4 py-3 items-center text-sm hover:bg-muted/20" data-testid={`export-audit-row-${i}`}>
                  <div className="col-span-4 truncate">
                    <span className="font-medium text-foreground">{log.object_name || 'Export'}</span>
                    {log.object_type && <Badge variant="outline" className="ml-2 text-[9px] capitalize">{log.object_type}</Badge>}
                  </div>
                  <div className="col-span-2 flex items-center gap-1.5">
                    <Icon className={`w-4 h-4 ${meta.color}`} />
                    <span className="text-xs">{meta.label}</span>
                  </div>
                  <div className="col-span-3 truncate text-muted-foreground" data-testid={`export-audit-user-${i}`}>{log.user_email || log.user_id || 'unknown'}</div>
                  <div className="col-span-3 text-muted-foreground text-xs">{when}</div>
                </div>
              );
            })}
          </div>
        )}
      </Card>
    </div>
  );
}
